import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { filter, first, withLatestFrom, map, tap } from 'rxjs/operators';
import { recordEvent } from '@mode/shared/util-tracing';

import {
  reportBuilderSetHasRendered,
  reportBuilderSetInViewport,
  reportBuilderVirtualRendered,
  reportBuilderVirtualScrolledRendered,
  reportViewerSetHasRendered,
  reportViewerSetInViewport,
  reportViewerSetVirtualEnabled,
  reportViewerVirtualRendered,
  reportViewerVirtualScrolledRendered,
} from './virtual-scroll.actions';
import { selectUserScrolled, selectViewportRendered } from './virtual-scroll.selectors';

@Injectable()
export class VirtualScrollEffects {
  viewerHasFinishedRendering$ = createEffect(() =>
    this.actions$.pipe(
      ofType(reportViewerSetInViewport, reportViewerSetHasRendered),
      withLatestFrom(this.store.select(selectViewportRendered), this.store.select(selectUserScrolled)),
      filter(([action, rendered, scrolled]) => rendered),
      map(([action, rendered, scrolled]) => {
        //if scrolled we go to scrollled event
        if (scrolled) {
          return reportViewerVirtualScrolledRendered();
        }
        return reportViewerVirtualRendered();
      }),
      first(), // Only want the first moment the report is "rendered"
      tap((action) => {
        recordEvent('report-rendering', 'virtual-report-rendered', {
          scrolled: action.type === reportViewerVirtualScrolledRendered.type,
        });
      })
    )
  );

  builderHasFinishedRendering$ = createEffect(() =>
    this.actions$.pipe(
      ofType(reportBuilderSetInViewport, reportBuilderSetHasRendered),
      withLatestFrom(this.store.select(selectViewportRendered), this.store.select(selectUserScrolled)),
      filter(([action, rendered, scrolled]) => rendered),
      map(([action, rendered, scrolled]) => {
        //if scrolled we go to scrollled event
        if (scrolled) {
          return reportBuilderVirtualScrolledRendered();
        }
        return reportBuilderVirtualRendered();
      }),
      first() // Only want the first moment the report is "rendered"
    )
  );

  viewerSetInViewport$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(reportViewerSetInViewport),
        tap((action) => recordEvent('report-rendering', 'viewport-update', { intersections: action.intersections }))
      ),
    {
      dispatch: false,
    }
  );

  viewerSetVirtualEnabled$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(reportViewerSetVirtualEnabled),
        tap((action) => recordEvent('report-rendering', 'begin-report-render', { virtualized: action.enabled }))
      ),
    {
      dispatch: false,
    }
  );

  constructor(private actions$: Actions, private store: Store) {}
}
